import React from 'react';
import type { DetailedRisk, SiteRagReport } from '../../../types/siteRag';
import { walkCitationText } from '../../../lib/walkCitationText';
import { hasMatrixCoords } from '../../../lib/hasMatrixCoords';
import { likelihoodScore, impactScore } from './RiskScatterChart';
import type { ReportTheme } from '../../../types/reportTheme';
import { useEffectiveReportTheme } from '../../../lib/useEffectiveReportTheme';
import { roleColor } from '../../../lib/reportRoleColors';
import { useFigureNumber } from './figureCounter';
import {
  EditorialBadge,
  EditorialDataTable,
  EditorialFigureCaption,
  EditorialRiskMatrix,
  type EditorialBadgeTone,
  type EditorialRiskItem,
  type RiskLevel,
} from './editorial';

const MONO = 'var(--report-body-font, inherit)';

interface Props {
  risks: DetailedRisk[];
  references?: SiteRagReport['references'];
  testIdPrefix?: string;
  /**
   * Report theme — drives every chrome colour via roleColor() and the
   * editorial primitives. Optional + back-compat: callers that don't thread
   * a theme (the 4 thin per-type risk-matrix sections) fall back to
   * useEffectiveReportTheme(), the same source SectionShell reads, so the
   * panel follows the active preset instead of pinning DEFAULT_THEME.
   */
  theme?: ReportTheme;
}

/** Likelihood → EditorialBadge tone (replaces filled pill backgrounds). */
const likelihoodTone = (level: DetailedRisk['riskLevel']): EditorialBadgeTone => {
  if (level === 'High') return 'negative';
  if (level === 'Medium') return 'warning';
  return 'positive';
};

/** Impact → EditorialBadge tone (replaces filled pill backgrounds). */
const impactTone = (level: DetailedRisk['impactLevel']): EditorialBadgeTone => {
  if (level === 'Red') return 'negative';
  if (level === 'Amber') return 'warning';
  return 'positive';
};

const impactLabel = (level: DetailedRisk['impactLevel']): string =>
  level === 'Red' ? 'High' : level === 'Amber' ? 'Medium' : 'Low';

/**
 * TypedRiskMatrixPanel — the live risk-matrix surface for the modern per-type
 * sections (Power / Water / Connectivity / Supply-Chain risk-matrix sections).
 *
 * SECTION-PORT (W0, Risk 14A + 14B): composed from the editorial primitives.
 *
 *   - 14A `EditorialRiskMatrix` — rendered ONLY for risks carrying genuine
 *     likelihood × impact ordinals: `DetailedRisk.riskLevel` (Low/Medium/High)
 *     × `DetailedRisk.impactLevel` (Green/Amber/Red), validated per-risk by
 *     `hasMatrixCoords` and mapped through the SAME canonical
 *     `likelihoodScore`/`impactScore` (→ 1/2/3) the previous Recharts scatter
 *     plotted. No ordinal is ever assigned to a risk that lacks one — risks
 *     failing the predicate appear only in the register + cards, and when NO
 *     risk passes, the matrix is omitted entirely (the 14B register is the
 *     documented fallback). The Recharts scatter is replaced because the
 *     editorial idiom requires pure / print-safe rendering (no re-measure at
 *     export); the inline-grid matrix is identical on-screen and in
 *     viewMode='export'.
 *   - 14B `EditorialDataTable` ranked register — risk / impact / summary, where
 *     the merged risk cell stacks the numbered name (ink) over the likelihood
 *     badge so the text-heavy impact + summary columns get the freed width
 *     (BUG #5). The numbered name still ties each row to its matrix chip and
 *     detail card. Mitigation detail stays on the numbered cards below (which
 *     keep the citation walking).
 */
export const TypedRiskMatrixPanel: React.FC<Props> = ({
  risks,
  references,
  testIdPrefix = 'risk-matrix',
  theme: themeProp,
}) => {
  const effectiveTheme = useEffectiveReportTheme();
  const theme = themeProp ?? effectiveTheme;
  const matrixFigureNumber = useFigureNumber(`riskMatrix:${testIdPrefix}`);

  // Genuine L×I ordinals only — keep each risk's 1-based index from the FULL
  // list so matrix chips, register rows and detail cards share one numbering.
  const matrixItems: EditorialRiskItem[] = risks.flatMap((risk, idx) =>
    hasMatrixCoords(risk)
      ? [{
          label: `${idx + 1} · ${risk.category}`,
          likelihood: likelihoodScore(risk.riskLevel) as RiskLevel,
          impact: impactScore(risk.impactLevel) as RiskLevel,
        }]
      : [],
  );
  const showMatrix = matrixItems.length > 0;

  if (risks.length === 0) return null;

  return (
    <div className="space-y-5" data-testid={`${testIdPrefix}-panel`}>
      {showMatrix && (
        <div
          data-span-columns
          data-paged-split-after="true"
          data-paged-split-kind="risk-matrix"
        >
          <EditorialRiskMatrix
            theme={theme}
            items={matrixItems}
            impactLabels={['Low impact', 'Med impact', 'High impact']}
          />
          <EditorialFigureCaption theme={theme} number={matrixFigureNumber}>
            Risk positioning by likelihood and impact. Numbered chips
            correspond to the register and detail cards below.
          </EditorialFigureCaption>
        </div>
      )}

      <div className="space-y-5" data-paged-split-suffix="true">
        <div data-span-columns className="overflow-x-auto" data-testid={`${testIdPrefix}-summary-table`}>
          <EditorialDataTable
            theme={theme}
            columns={[
              { header: 'Risk' },
              { header: 'Impact', width: '13%' },
              { header: 'Summary' },
            ]}
            rows={risks.map((risk, idx) => [
              // BUG #5 — Risk + Likelihood merged into ONE cell (risk name on top,
              // likelihood badge stacked beneath) so the text-heavy Impact +
              // Summary columns reclaim the freed width.
              <div key="risk" className="flex flex-col items-start gap-1.5">
                <span style={{ fontWeight: 600, color: roleColor(theme, 'ink') }}>
                  {`${idx + 1}. ${risk.category}`}
                </span>
                <EditorialBadge theme={theme} tone={likelihoodTone(risk.riskLevel)} variant="outline">
                  {risk.riskLevel}
                </EditorialBadge>
              </div>,
              <EditorialBadge key="impact" theme={theme} tone={impactTone(risk.impactLevel)} variant="outline">
                {impactLabel(risk.impactLevel)}
              </EditorialBadge>,
              walkCitationText(risk.reason, references),
            ])}
          />
          <EditorialFigureCaption theme={theme} number={1} prefix="Table">
            Risk register – likelihood and impact band per identified risk;
            mitigation detail follows in the numbered cards.
          </EditorialFigureCaption>
        </div>

        <ol
          className="space-y-3 list-none grid grid-cols-1 lg:grid-cols-2 gap-3"
          data-testid={`${testIdPrefix}-cards`}
          aria-label="Risk detail cards"
        >
          {risks.map((risk, idx) => (
            <li
              key={`${risk.category}-${idx}`}
              className="pt-3 space-y-2"
              style={{ borderTop: `1px solid ${roleColor(theme, 'rule')}` }}
              data-testid={`${testIdPrefix}-card-${idx + 1}`}
            >
              <div className="flex items-start justify-between gap-2">
                <div className="flex items-baseline gap-2 min-w-0">
                  <span
                    className={`${theme.headingScale === 'compact' ? 'text-xs' : 'text-sm'} font-bold tabular-nums shrink-0`}
                    style={{ color: roleColor(theme, 'muted') }}
                  >
                    {idx + 1}.
                  </span>
                  <h4 className="text-sm font-bold m-0" style={{ color: roleColor(theme, 'ink') }}>{risk.category}</h4>
                </div>
                <div className="flex gap-1 shrink-0 flex-wrap justify-end">
                  <EditorialBadge theme={theme} tone={likelihoodTone(risk.riskLevel)} variant="outline">
                    {risk.riskLevel}
                  </EditorialBadge>
                  <EditorialBadge theme={theme} tone={impactTone(risk.impactLevel)} variant="outline">
                    {impactLabel(risk.impactLevel)} impact
                  </EditorialBadge>
                </div>
              </div>
              <p className="text-xs leading-relaxed m-0" style={{ color: roleColor(theme, 'body') }}>
                {walkCitationText(risk.reason, references)}
              </p>
              {risk.action?.trim() && (
                <p className="text-xs leading-relaxed m-0 pt-2" style={{ color: roleColor(theme, 'body'), borderTop: `1px solid ${roleColor(theme, 'hair')}` }}>
                  <span
                    className="font-bold uppercase tracking-widest mr-1"
                    style={{ fontFamily: MONO, fontSize: 9, color: roleColor(theme, 'muted') }}
                  >
                    Mitigation:
                  </span>
                  {walkCitationText(risk.action, references)}
                </p>
              )}
            </li>
          ))}
        </ol>
      </div>
    </div>
  );
};
